import {
  AiToolkitToolName,
  addNoteToCollectionInputSchema,
  addNoteToCollectionOutputSchema,
  createNoteInputSchema,
  createNoteOutputSchema,
  createAssistantTaskInputSchema,
  createAssistantTaskOutputSchema,
  getAssistantFollowUpInputSchema,
  getAssistantFollowUpOutputSchema,
  getAssistantProjectInputSchema,
  getAssistantProjectOutputSchema,
  getAssistantTaskInputSchema,
  getAssistantTaskOutputSchema,
  getCalendarEventInputSchema,
  getCalendarEventOutputSchema,
  getCollectionInputSchema,
  getCollectionOutputSchema,
  getNoteInputSchema,
  getNoteOutputSchema,
  listCollectionItemsInputSchema,
  listCollectionItemsOutputSchema,
  navigateToCollectionInputSchema,
  navigateToCollectionOutputSchema,
  navigateToNoteInputSchema,
  navigateToNoteOutputSchema,
  searchAssistantFollowUpsInputSchema,
  searchAssistantFollowUpsOutputSchema,
  searchAssistantProjectsInputSchema,
  searchAssistantProjectsOutputSchema,
  searchAssistantTasksInputSchema,
  searchAssistantTasksOutputSchema,
  searchCalendarEventsInputSchema,
  searchCalendarEventsOutputSchema,
  searchCollectionsInputSchema,
  searchCollectionsOutputSchema,
  searchNotesInputSchema,
  searchNotesOutputSchema,
  removeNoteFromCollectionInputSchema,
  removeNoteFromCollectionOutputSchema,
  updateNoteInputSchema,
  updateNoteOutputSchema,
} from "./schemas";
import type {
  AddNoteToCollectionInput,
  AddNoteToCollectionOutput,
  CreateNoteInput,
  CreateNoteOutput,
  CreateAssistantTaskInput,
  CreateAssistantTaskOutput,
  GetAssistantFollowUpInput,
  GetAssistantFollowUpOutput,
  GetAssistantProjectInput,
  GetAssistantProjectOutput,
  GetAssistantTaskInput,
  GetAssistantTaskOutput,
  GetCalendarEventInput,
  GetCalendarEventOutput,
  GetCollectionInput,
  GetCollectionOutput,
  GetNoteInput,
  GetNoteOutput,
  ListCollectionItemsInput,
  ListCollectionItemsOutput,
  NavigateToCollectionInput,
  NavigateToCollectionOutput,
  NavigateToNoteInput,
  NavigateToNoteOutput,
  SearchAssistantFollowUpsInput,
  SearchAssistantFollowUpsOutput,
  SearchAssistantProjectsInput,
  SearchAssistantProjectsOutput,
  SearchAssistantTasksInput,
  SearchAssistantTasksOutput,
  SearchCalendarEventsInput,
  SearchCalendarEventsOutput,
  SearchCollectionsInput,
  SearchCollectionsOutput,
  SearchNotesInput,
  SearchNotesOutput,
  RemoveNoteFromCollectionInput,
  RemoveNoteFromCollectionOutput,
  UpdateNoteInput,
  UpdateNoteOutput,
} from "./types";
import type { z } from "zod";

export type AiToolkitToolMetadata<TInput, TOutput> = {
  description: string;
  inputSchema: z.ZodType<TInput>;
  outputSchema: z.ZodType<TOutput>;
};

export type AiToolkitToolMetadataByName = {
  [AiToolkitToolName.GetNote]: AiToolkitToolMetadata<GetNoteInput, GetNoteOutput>;
  [AiToolkitToolName.SearchNotes]: AiToolkitToolMetadata<SearchNotesInput, SearchNotesOutput>;
  [AiToolkitToolName.CreateNote]: AiToolkitToolMetadata<CreateNoteInput, CreateNoteOutput>;
  [AiToolkitToolName.UpdateNote]: AiToolkitToolMetadata<UpdateNoteInput, UpdateNoteOutput>;
  [AiToolkitToolName.GetAssistantTask]: AiToolkitToolMetadata<
    GetAssistantTaskInput,
    GetAssistantTaskOutput
  >;
  [AiToolkitToolName.SearchAssistantTasks]: AiToolkitToolMetadata<
    SearchAssistantTasksInput,
    SearchAssistantTasksOutput
  >;
  [AiToolkitToolName.CreateAssistantTask]: AiToolkitToolMetadata<
    CreateAssistantTaskInput,
    CreateAssistantTaskOutput
  >;
  [AiToolkitToolName.GetAssistantProject]: AiToolkitToolMetadata<
    GetAssistantProjectInput,
    GetAssistantProjectOutput
  >;
  [AiToolkitToolName.SearchAssistantProjects]: AiToolkitToolMetadata<
    SearchAssistantProjectsInput,
    SearchAssistantProjectsOutput
  >;
  [AiToolkitToolName.GetAssistantFollowUp]: AiToolkitToolMetadata<
    GetAssistantFollowUpInput,
    GetAssistantFollowUpOutput
  >;
  [AiToolkitToolName.SearchAssistantFollowUps]: AiToolkitToolMetadata<
    SearchAssistantFollowUpsInput,
    SearchAssistantFollowUpsOutput
  >;
  [AiToolkitToolName.GetCalendarEvent]: AiToolkitToolMetadata<
    GetCalendarEventInput,
    GetCalendarEventOutput
  >;
  [AiToolkitToolName.SearchCalendarEvents]: AiToolkitToolMetadata<
    SearchCalendarEventsInput,
    SearchCalendarEventsOutput
  >;
  [AiToolkitToolName.GetCollection]: AiToolkitToolMetadata<GetCollectionInput, GetCollectionOutput>;
  [AiToolkitToolName.SearchCollections]: AiToolkitToolMetadata<
    SearchCollectionsInput,
    SearchCollectionsOutput
  >;
  [AiToolkitToolName.ListCollectionItems]: AiToolkitToolMetadata<
    ListCollectionItemsInput,
    ListCollectionItemsOutput
  >;
  [AiToolkitToolName.AddNoteToCollection]: AiToolkitToolMetadata<
    AddNoteToCollectionInput,
    AddNoteToCollectionOutput
  >;
  [AiToolkitToolName.RemoveNoteFromCollection]: AiToolkitToolMetadata<
    RemoveNoteFromCollectionInput,
    RemoveNoteFromCollectionOutput
  >;
  [AiToolkitToolName.NavigateToNote]: AiToolkitToolMetadata<
    NavigateToNoteInput,
    NavigateToNoteOutput
  >;
  [AiToolkitToolName.NavigateToCollection]: AiToolkitToolMetadata<
    NavigateToCollectionInput,
    NavigateToCollectionOutput
  >;
};

/**
 * Authoritative per-tool metadata for the AI toolkit. Holds the description
 * the model sees plus the input/output schema references. Renderer-side tool
 * implementations and main-process AI SDK adapters both read from this
 * registry so descriptions and schemas have a single source of truth.
 */
export const aiToolkitToolMetadataByName: AiToolkitToolMetadataByName = {
  [AiToolkitToolName.GetNote]: {
    description: [
      "Get one note from the local Mem client store by note ID.",
      "This only reads local data and reports an error when the note or requested content is not available locally.",
    ].join(" "),
    inputSchema: getNoteInputSchema,
    outputSchema: getNoteOutputSchema,
  },
  [AiToolkitToolName.SearchNotes]: {
    description: [
      "Search the user's Mem notes by query.",
      "Uses Mem's smart-search engine, matching against note titles and body content semantically.",
      "Can optionally include a local plaintext preview of each matched note.",
    ].join(" "),
    inputSchema: searchNotesInputSchema,
    outputSchema: searchNotesOutputSchema,
  },
  [AiToolkitToolName.CreateNote]: {
    description: [
      "Create a new Mem note in the local client.",
      "Can set an optional title and MDX/Markdown body, and can optionally navigate the UI to the new note.",
    ].join(" "),
    inputSchema: createNoteInputSchema,
    outputSchema: createNoteOutputSchema,
  },
  [AiToolkitToolName.UpdateNote]: {
    description: [
      "Replace one local Mem note's content by note ID.",
      "The mdx input is the complete replacement note content, including title heading if desired.",
    ].join(" "),
    inputSchema: updateNoteInputSchema,
    outputSchema: updateNoteOutputSchema,
  },
  [AiToolkitToolName.GetAssistantTask]: {
    description: [
      "Get one assistant task from the local Mem client store by task ID.",
      "This only reads local data and reports an error when the task is not available locally.",
    ].join(" "),
    inputSchema: getAssistantTaskInputSchema,
    outputSchema: getAssistantTaskOutputSchema,
  },
  [AiToolkitToolName.SearchAssistantTasks]: {
    description: [
      "Search assistant tasks in the local Mem client store.",
      "This reads locally synced assistant task data and pending local create projections.",
    ].join(" "),
    inputSchema: searchAssistantTasksInputSchema,
    outputSchema: searchAssistantTasksOutputSchema,
  },
  [AiToolkitToolName.CreateAssistantTask]: {
    description: [
      "Create a new assistant task by submitting a local assistant change request.",
      "An optional follow-up reminder can be created in the same request group.",
      "The returned taskId and followUpId are stable IDs that can be passed back to assistant task and follow-up tools immediately while reconciliation is pending.",
    ].join(" "),
    inputSchema: createAssistantTaskInputSchema,
    outputSchema: createAssistantTaskOutputSchema,
  },
  [AiToolkitToolName.GetAssistantProject]: {
    description: [
      "Get one assistant project from the local Mem client store by project ID.",
      "This only reads local data and reports an error when the project is not available locally.",
    ].join(" "),
    inputSchema: getAssistantProjectInputSchema,
    outputSchema: getAssistantProjectOutputSchema,
  },
  [AiToolkitToolName.SearchAssistantProjects]: {
    description: [
      "Search assistant projects in the local Mem client store.",
      "This only reads locally synced assistant project data.",
    ].join(" "),
    inputSchema: searchAssistantProjectsInputSchema,
    outputSchema: searchAssistantProjectsOutputSchema,
  },
  [AiToolkitToolName.GetAssistantFollowUp]: {
    description: [
      "Get one assistant follow-up from the local Mem client store by follow-up ID.",
      "This only reads local data and reports an error when the follow-up is not available locally.",
    ].join(" "),
    inputSchema: getAssistantFollowUpInputSchema,
    outputSchema: getAssistantFollowUpOutputSchema,
  },
  [AiToolkitToolName.SearchAssistantFollowUps]: {
    description: [
      "Search assistant follow-ups in the local Mem client store.",
      "This only reads locally synced ASSISTANT_FOLLOW_UP data.",
    ].join(" "),
    inputSchema: searchAssistantFollowUpsInputSchema,
    outputSchema: searchAssistantFollowUpsOutputSchema,
  },
  [AiToolkitToolName.GetCalendarEvent]: {
    description: [
      "Get one calendar event from the local Mem client store by event ID.",
      "Includes timing, meeting URL, calendar labels, attendee metadata, and associated note reference when available locally.",
    ].join(" "),
    inputSchema: getCalendarEventInputSchema,
    outputSchema: getCalendarEventOutputSchema,
  },
  [AiToolkitToolName.SearchCalendarEvents]: {
    description: [
      "Search calendar events in the local Mem client store by time range and optional query.",
      "The query matches event title, meeting URL, calendar labels, and associated note title.",
      "When no range is supplied, the host searches a near-future local window.",
    ].join(" "),
    inputSchema: searchCalendarEventsInputSchema,
    outputSchema: searchCalendarEventsOutputSchema,
  },
  [AiToolkitToolName.GetCollection]: {
    description: [
      "Get one collection from the local Mem client store by collection ID.",
      "Includes sharing, favorite, recency, path, and item-count metadata.",
    ].join(" "),
    inputSchema: getCollectionInputSchema,
    outputSchema: getCollectionOutputSchema,
  },
  [AiToolkitToolName.SearchCollections]: {
    description: [
      "Search collections in the local Mem client store.",
      "The query matches collection titles, with optional sharing and ownership filters.",
    ].join(" "),
    inputSchema: searchCollectionsInputSchema,
    outputSchema: searchCollectionsOutputSchema,
  },
  [AiToolkitToolName.ListCollectionItems]: {
    description: [
      "List local items inside one Mem collection by collection ID.",
      "For note items, this can optionally include local plaintext note previews.",
    ].join(" "),
    inputSchema: listCollectionItemsInputSchema,
    outputSchema: listCollectionItemsOutputSchema,
  },
  [AiToolkitToolName.AddNoteToCollection]: {
    description: [
      "Add a local Mem note to a local Mem collection.",
      "This mutates collection membership and returns whether the note was newly added.",
    ].join(" "),
    inputSchema: addNoteToCollectionInputSchema,
    outputSchema: addNoteToCollectionOutputSchema,
  },
  [AiToolkitToolName.RemoveNoteFromCollection]: {
    description: [
      "Remove a local Mem note from a local Mem collection.",
      "This mutates collection membership and returns whether the note was present and removed.",
    ].join(" "),
    inputSchema: removeNoteFromCollectionInputSchema,
    outputSchema: removeNoteFromCollectionOutputSchema,
  },
  [AiToolkitToolName.NavigateToNote]: {
    description: [
      "Navigate the Mem client UI to a local note by note ID.",
      "Use this only when the user wants to open or view a specific note in the app.",
    ].join(" "),
    inputSchema: navigateToNoteInputSchema,
    outputSchema: navigateToNoteOutputSchema,
  },
  [AiToolkitToolName.NavigateToCollection]: {
    description: [
      "Navigate the Mem client UI to a local collection by collection ID.",
      "Use this only when the user wants to open or view a specific collection in the app.",
    ].join(" "),
    inputSchema: navigateToCollectionInputSchema,
    outputSchema: navigateToCollectionOutputSchema,
  },
};
